import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ChatCards from "@/components/ChatCards";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL } from "@/site";
import { useLang } from "@/i18n";

const MAP_EMBED =
  "https://www.openstreetmap.org/export/embed.html?bbox=119.95%2C29.20%2C120.20%2C29.42&layer=mapnik&marker=29.3068%2C120.0756";
const DIRECTIONS =
  "https://www.google.com/maps/search/?api=1&query=Shuguang+Mansion+Yiwu+Zhejiang+China";

const Contact = () => {
  const { t } = useLang();

  const methods = [
    { Icon: Mail, label: t.contact.emailL, value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email-card" },
    { Icon: Phone, label: t.contact.phoneL, value: PHONE_DISPLAY, href: PHONE_TEL, testid: "contact-phone-card" },
    { Icon: MapPin, label: t.contact.addressL, value: t.contact.addressV, href: DIRECTIONS, testid: "contact-address-card" },
  ];

  return (
    <div className="min-h-screen bg-bone font-sans text-ink antialiased">
      <Seo title={t.seo.contact.title} description={t.seo.contact.description} />
      <Header />
      <main className="pt-[210px] md:pt-[240px]">
        <section className="mx-auto max-w-7xl px-6 md:px-12" data-testid="contact-section">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.contact.overline}</p>
            <h1 data-testid="contact-heading" className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-6xl">
              {t.contact.h1a} <em className="italic text-clay">{t.contact.h1b}</em>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg">{t.contact.sub}</p>
          </Reveal>

          <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-3">
            {methods.map((m, i) => (
              <Reveal key={m.testid} delay={i * 0.1}>
                <a
                  href={m.href}
                  target={m.href.startsWith("http") ? "_blank" : undefined}
                  rel={m.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={m.testid}
                  className="group flex h-full flex-col items-center rounded-3xl border border-ink/10 bg-white p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(0,0,0,0.10)]"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink text-clay shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3">
                    <m.Icon className="h-6 w-6" />
                  </div>
                  <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{m.label}</p>
                  <p className="mt-2 font-serif text-lg leading-snug text-ink transition-colors duration-300 group-hover:text-clay">{m.value}</p>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div data-testid="contact-map-card" className="mx-auto mt-8 max-w-5xl overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <iframe
                title="HHB Clothing Co — Yiwu office map"
                data-testid="contact-map"
                src={MAP_EMBED}
                loading="lazy"
                className="h-[340px] w-full border-0 md:h-[420px]"
              />
              <div className="flex flex-col items-center justify-between gap-4 border-t border-ink/10 p-6 sm:flex-row">
                <p className="flex items-start gap-2 text-sm leading-relaxed text-ink/70">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                  {t.contact.addressV}
                </p>
                <a
                  href={DIRECTIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay"
                >
                  {t.contact.directionsCta}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-12 text-center">
            <a href="/#quote" data-testid="contact-quote-cta" className="group inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay">
              {t.contact.quoteCta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
            </a>
          </Reveal>
        </section>

        <ChatCards />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default Contact;
